import { useMemo } from 'react';
import { Card } from '../ui';

/**
 * AgentsStatsPanel
 *
 * Header summary card for the Friendly Agents page. Pulls global aggregates
 * from `/backend/agents/stats` and surfaces:
 *
 *  1) Headline numbers (total / resolved / unresolved / profile-complete /
 *     no-owner) so an operator can see the directory's health at a glance.
 *  2) Per-owner breakdown sorted by total agents, with a stacked bar that
 *     shows resolved-vs-unresolved proportions and a badge for the count of
 *     owners whose agents have already sent messages.
 *  3) Per-region counts (handy when one owner like "Люба" spans multiple
 *     regions — Турция, Сочи, etc).
 *  4) Observed-messages totals + last seen timestamp.
 *
 * No business logic in here — every number comes from the backend. The only
 * derivation is the stacked-bar geometry and percentages.
 */
const formatPercent = (value, total) => {
  if (!total || total <= 0) return 0;
  return Math.round((value / total) * 100);
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
};

const AgentsStatsPanel = ({ stats, isLoading }) => {
  const totals = stats?.totals || {};
  const owners = stats?.owners || [];
  const regions = stats?.regions || [];
  const messages = stats?.messages || { total: 0, withMessages: 0, lastMessageAt: null };
  const sources = stats?.sources || {};

  const topOwners = useMemo(() => owners.slice(0, 6), [owners]);
  const otherOwnersCount = Math.max(owners.length - topOwners.length, 0);

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 text-sm text-gray-500">Loading directory stats…</div>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const headlineCards = [
    {
      label: 'Total agents',
      value: totals.total || 0,
      sub: `${totals.profileComplete || 0} with full profile`,
      tone: 'blue',
    },
    {
      label: 'Resolved (user_id)',
      value: totals.resolved || 0,
      sub: `${totals.profilePartial || 0} user_id only`,
      tone: 'green',
    },
    {
      label: 'Unresolved',
      value: totals.unresolved || 0,
      sub: 'No user_id yet',
      tone: totals.unresolved ? 'yellow' : 'gray',
    },
    {
      label: 'Tracked messages',
      value: messages.total || 0,
      sub: `${messages.withMessages || 0} agents have sent`,
      tone: 'purple',
    },
  ];

  const toneClass = (tone) => ({
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    gray: 'border-gray-200 bg-gray-50',
    purple: 'border-purple-200 bg-purple-50',
  }[tone] || 'border-gray-200 bg-gray-50');

  return (
    <Card>
      <div className="p-5 space-y-5">
        <div className="flex items-baseline justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Directory at a glance</h2>
            <p className="text-xs text-gray-500">
              Aggregated across every friendly agent in the database, regardless of the
              search/filter applied to the table below.
            </p>
          </div>
          <div className="text-xs text-gray-500">
            Last seen message: <span className="font-medium text-gray-700">{formatTimestamp(messages.lastMessageAt)}</span>
          </div>
        </div>

        {/* Headline cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {headlineCards.map((card) => (
            <div key={card.label} className={`rounded-lg border p-4 ${toneClass(card.tone)}`}>
              <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
              <div className="mt-1 text-3xl font-semibold text-gray-900">{card.value}</div>
              <div className="mt-1 text-xs text-gray-600">{card.sub}</div>
            </div>
          ))}
        </div>

        {/* Owner breakdown + region split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-gray-900">By owner</h3>
              <span className="text-xs text-gray-500">{owners.length} owner{owners.length === 1 ? '' : 's'}</span>
            </div>
            {owners.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">No agents assigned to an owner yet.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {topOwners.map((row) => {
                  const resolvedPct = formatPercent(row.resolved, row.total);
                  return (
                    <li key={row.owner} className="px-4 py-3 flex items-center gap-4">
                      <div className="w-32 shrink-0">
                        <div className="text-sm font-medium text-gray-900 truncate" title={row.owner}>{row.owner}</div>
                        {Array.isArray(row.regions) && row.regions.length > 0 ? (
                          <div className="text-xs text-gray-500 truncate">
                            {row.regions.join(', ')}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">no region</div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
                          <div
                            className="h-full bg-green-500"
                            style={{ width: `${resolvedPct}%` }}
                            title={`Resolved ${row.resolved}/${row.total}`}
                          />
                          <div
                            className="h-full bg-yellow-400"
                            style={{ width: `${100 - resolvedPct}%` }}
                            title={`Unresolved ${row.unresolved}/${row.total}`}
                          />
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-green-500" />
                            {row.resolved} resolved
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-yellow-400" />
                            {row.unresolved} unresolved
                          </span>
                          {row.withMessages > 0 ? (
                            <span className="inline-flex items-center gap-1 text-purple-700">
                              <span className="h-2 w-2 rounded-full bg-purple-500" />
                              {row.withMessages} sent messages
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">{row.total}</div>
                        <div className="text-xs text-gray-500">{resolvedPct}% resolved</div>
                      </div>
                    </li>
                  );
                })}
                {otherOwnersCount > 0 && (
                  <li className="px-4 py-2 text-xs text-gray-500">
                    +{otherOwnersCount} more owner{otherOwnersCount === 1 ? '' : 's'} not shown
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">By region</h3>
            </div>
            {regions.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">No region data yet.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {regions.slice(0, 8).map((row) => (
                  <li key={row.ownerRegion} className="px-4 py-2 flex items-center justify-between">
                    <span className="text-sm text-gray-700">{row.ownerRegion === '—' ? <em className="text-gray-400">unspecified</em> : row.ownerRegion}</span>
                    <span className="text-sm font-medium text-gray-900">{row.total}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
              <div className="flex justify-between"><span>No owner</span><span>{totals.noOwner || 0}</span></div>
              <div className="flex justify-between"><span>Sources (top)</span><span>{Object.keys(sources).length}</span></div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AgentsStatsPanel;